"use client";

import { c as _c } from "react/compiler-runtime";
import { Fragment, useState } from "react";
import { useEditorStore } from "@/stores/editor-store";
import { useEditorState } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEditorBackend } from "@/lib/editor-backend/context";
import { useChapterImageInsert } from "@/hooks/use-chapter-image-insert";
import { PublishStoryPromptDialog } from "@/components/editor/publish-story-prompt-dialog";
import { isStoryPubliclyVisible, shouldPromptStoryPublish } from "@/components/editor/publish-prompt-logic";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { Editor } from "@tiptap/react";
import { Bold, Italic, Underline, Strikethrough, Heading1, Heading2, Heading3, AlignLeft, AlignCenter, AlignRight, List, ListOrdered, Quote, Undo2, Redo2, Minus, ImagePlus, MoreHorizontal, Send, CheckCircle2, Loader2, StickyNote } from "lucide-react";
import { EditorDesktopWritingTools } from "./editor-desktop-writing-tools";
import { ChapterSettingsDialog } from "@/components/editor/chapter-settings-dialog";
import { trackRedditEvent } from "@/lib/analytics/client";
import { ReleaseScheduleButton } from "@/components/editor/releases/release-schedule-button";
type ChapterStatus = "draft" | "published";
interface EditorToolbarProps {
  editor: Editor | null;
  chapterId: string;
  storyId: string;
  chapterTitle: string;
  /** Current end-of-chapter author's note (TipTap JSON), if any. */
  authorNote?: Record<string, unknown> | null;
  status: ChapterStatus;
  scheduledReleaseAt?: string | null;
  onChangeStatus: (status: ChapterStatus) => Promise<boolean>;
  isTogglingStatus: boolean;
  saveError: string | null;
  onSave: () => Promise<boolean>;
  /**
   * Story-level publish state, used to warn the author when publishing a
   * chapter won't reach readers because the story itself isn't public yet.
   * Optional with safe defaults so the `/try` demo (which never publishes a
   * real story) can omit them.
   */
  storyPublished?: boolean;
  storyVisibility?: "public" | "private";
  hidePublication?: boolean;
  isNotes?: boolean;
  publicationControls?: React.ReactNode;
  documentControls?: React.ReactNode;
  saveState?: {
    isDirty: boolean;
    isSaving: boolean;
    wordCount: number;
  };
}
type Tool = {
  id: string;
  label: string;
  shortcut?: string;
  icon: React.ReactNode;
  run: () => void;
  active?: boolean;
  disabled?: boolean;
};
export function EditorToolbar(t0) {
  const $ = _c(195);
  const {
    editor,
    chapterId,
    storyId,
    chapterTitle,
    authorNote,
    status,
    scheduledReleaseAt,
    onChangeStatus,
    isTogglingStatus,
    saveError,
    onSave,
    storyPublished: t1,
    storyVisibility: t2,
    hidePublication: t3,
    isNotes: t4,
    publicationControls,
    documentControls,
    saveState
  } = t0;
  const storyPublished = t1 === undefined ? false : t1;
  const storyVisibility = t2 === undefined ? "private" : t2;
  const hidePublication = t3 === undefined ? false : t3;
  const isNotes = t4 === undefined ? false : t4;
  const sharedSaveState = useEditorStore();
  const {
    isDirty,
    isSaving,
    wordCount
  } = saveState ?? sharedSaveState;
  const backend = useEditorBackend();
  const [isScheduling, setIsScheduling] = useState(false);
  let t5;
  if ($[0] !== storyPublished || $[1] !== storyVisibility) {
    t5 = () => isStoryPubliclyVisible(storyPublished, storyVisibility);
    $[0] = storyPublished;
    $[1] = storyVisibility;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const [storyIsPublic, setStoryIsPublic] = useState(t5);
  const [showPublishStoryPrompt, setShowPublishStoryPrompt] = useState(false);
  const [showChapterSettings, setShowChapterSettings] = useState(false);
  const {
    isUploading: isUploadingImage,
    inputRef: imageInputRef,
    openPicker: openImagePicker,
    handleInputChange: handleImageInputChange
  } = useChapterImageInsert(editor, storyId);
  let t6;
  if ($[3] !== backend || $[4] !== onChangeStatus || $[5] !== status || $[6] !== storyIsPublic) {
    t6 = async function handleToggleStatus() {
      const next = status === "published" ? "draft" : "published";
      if (!(await onChangeStatus(next))) {
        return;
      }
      if (next === "published" && !backend.isDemo) {
        trackRedditEvent("PublishChapter");
      }
      if (shouldPromptStoryPublish({
        nextStatus: next,
        storyIsPublic,
        isDemo: backend.isDemo
      })) {
        setShowPublishStoryPrompt(true);
      }
    };
    $[3] = backend;
    $[4] = onChangeStatus;
    $[5] = status;
    $[6] = storyIsPublic;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleToggleStatus = t6;
  let t7;
  if ($[8] !== editor) {
    t7 = {
      editor,
      selector: _temp
    };
    $[8] = editor;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const editorState = useEditorState(t7);
  if (!editor || !editorState) {
    return null;
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Bold className="h-4 w-4" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== editor) {
    t9 = () => editor.chain().focus().toggleBold().run();
    $[11] = editor;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== editorState.isBold || $[14] !== t9) {
    t10 = {
      id: "bold",
      label: "Bold",
      shortcut: "\u2318B",
      icon: t8,
      run: t9,
      active: editorState.isBold
    };
    $[13] = editorState.isBold;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Italic className="h-4 w-4" />;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== editor) {
    t12 = () => editor.chain().focus().toggleItalic().run();
    $[17] = editor;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== editorState.isItalic || $[20] !== t12) {
    t13 = {
      id: "italic",
      label: "Italic",
      shortcut: "\u2318I",
      icon: t11,
      run: t12,
      active: editorState.isItalic
    };
    $[19] = editorState.isItalic;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Underline className="h-4 w-4" />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== editor) {
    t15 = () => editor.chain().focus().toggleUnderline().run();
    $[23] = editor;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== editorState.isUnderline || $[26] !== t15) {
    t16 = {
      id: "underline",
      label: "Underline",
      shortcut: "\u2318U",
      icon: t14,
      run: t15,
      active: editorState.isUnderline
    };
    $[25] = editorState.isUnderline;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Strikethrough className="h-4 w-4" />;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== editor) {
    t18 = () => editor.chain().focus().toggleStrike().run();
    $[29] = editor;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== editorState.isStrike || $[32] !== t18) {
    t19 = {
      id: "strike",
      label: "Strikethrough",
      icon: t17,
      run: t18,
      active: editorState.isStrike
    };
    $[31] = editorState.isStrike;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t10 || $[35] !== t13 || $[36] !== t16 || $[37] !== t19) {
    t20 = [t10, t13, t16, t19];
    $[34] = t10;
    $[35] = t13;
    $[36] = t16;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const textTools = t20;
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Heading1 className="h-4 w-4" />;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== editor) {
    t22 = () => editor.chain().focus().toggleHeading({
      level: 1
    }).run();
    $[40] = editor;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let T0;
  let T1;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  if ($[42] !== authorNote || $[43] !== backend.isDemo || $[44] !== chapterId || $[45] !== chapterTitle || $[46] !== documentControls || $[47] !== editor || $[48] !== editorState.canRedo || $[49] !== editorState.canUndo || $[50] !== editorState.isAlignedCenter || $[51] !== editorState.isAlignedLeft || $[52] !== editorState.isAlignedRight || $[53] !== editorState.isBlockquote || $[54] !== editorState.isBulletList || $[55] !== editorState.isH1 || $[56] !== editorState.isH2 || $[57] !== editorState.isH3 || $[58] !== editorState.isOrderedList || $[59] !== handleToggleStatus || $[60] !== hidePublication || $[61] !== isDirty || $[62] !== isNotes || $[63] !== isSaving || $[64] !== isScheduling || $[65] !== isTogglingStatus || $[66] !== isUploadingImage || $[67] !== onSave || $[68] !== openImagePicker || $[69] !== publicationControls || $[70] !== saveError || $[71] !== scheduledReleaseAt || $[72] !== status || $[73] !== storyId || $[74] !== t22 || $[75] !== textTools || $[76] !== wordCount) {
    const headingTools = [{
      id: "h1",
      label: "Heading 1",
      icon: t21,
      run: t22,
      active: editorState.isH1
    }, {
      id: "h2",
      label: "Heading 2",
      icon: <Heading2 className="h-4 w-4" />,
      run: () => editor.chain().focus().toggleHeading({
        level: 2
      }).run(),
      active: editorState.isH2
    }, {
      id: "h3",
      label: "Heading 3",
      icon: <Heading3 className="h-4 w-4" />,
      run: () => editor.chain().focus().toggleHeading({
        level: 3
      }).run(),
      active: editorState.isH3
    }];
    const alignmentTools = [{
      id: "alignLeft",
      label: "Align left",
      icon: <AlignLeft className="h-4 w-4" />,
      run: () => editor.chain().focus().setTextAlign("left").run(),
      active: editorState.isAlignedLeft
    }, {
      id: "alignCenter",
      label: "Align center",
      icon: <AlignCenter className="h-4 w-4" />,
      run: () => editor.chain().focus().setTextAlign("center").run(),
      active: editorState.isAlignedCenter
    }, {
      id: "alignRight",
      label: "Align right",
      icon: <AlignRight className="h-4 w-4" />,
      run: () => editor.chain().focus().setTextAlign("right").run(),
      active: editorState.isAlignedRight
    }];
    const blockTools = [{
      id: "bulletList",
      label: "Bullet list",
      icon: <List className="h-4 w-4" />,
      run: () => editor.chain().focus().toggleBulletList().run(),
      active: editorState.isBulletList
    }, {
      id: "orderedList",
      label: "Numbered list",
      icon: <ListOrdered className="h-4 w-4" />,
      run: () => editor.chain().focus().toggleOrderedList().run(),
      active: editorState.isOrderedList
    }, {
      id: "blockquote",
      label: "Block quote",
      icon: <Quote className="h-4 w-4" />,
      run: () => editor.chain().focus().toggleBlockquote().run(),
      active: editorState.isBlockquote
    }, {
      id: "hr",
      label: "Horizontal rule",
      icon: <Minus className="h-4 w-4" />,
      run: () => editor.chain().focus().setHorizontalRule().run()
    }];
    const insertTools = [{
      id: "image",
      label: "Insert image",
      icon: isUploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />,
      run: openImagePicker,
      disabled: isUploadingImage
    }];
    const historyTools = [{
      id: "undo",
      label: "Undo",
      shortcut: "\u2318Z",
      icon: <Undo2 className="h-4 w-4" />,
      run: () => editor.chain().focus().undo().run(),
      disabled: !editorState.canUndo
    }, {
      id: "redo",
      label: "Redo",
      shortcut: "\u2318\u21E7Z",
      icon: <Redo2 className="h-4 w-4" />,
      run: () => editor.chain().focus().redo().run(),
      disabled: !editorState.canRedo
    }];
    const overflowGroups = [headingTools, alignmentTools, blockTools, insertTools, historyTools];
    t29 = "bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-700 px-3 py-1.5";
    const t33 = hidePublication && "flex-wrap gap-1";
    let t34;
    if ($[89] !== t33) {
      t34 = cn("flex items-center justify-between mb-1", t33);
      $[89] = t33;
      $[90] = t34;
    } else {
      t34 = $[90];
    }
    const t35 = isNotes ? "Notes" : chapterTitle;
    const t36 = isNotes ? "Notes" : `${chapterTitle} – ${status === "published" ? "Published" : "Draft"}`;
    let t37;
    if ($[91] !== t35 || $[92] !== t36) {
      t37 = <h2 className="text-sm font-medium text-zinc-600 dark:text-zinc-400 truncate" title={t35}>{t36}</h2>;
      $[91] = t35;
      $[92] = t36;
      $[93] = t37;
    } else {
      t37 = $[93];
    }
    let t38;
    if ($[94] !== authorNote || $[95] !== isNotes) {
      t38 = !isNotes && <button type="button" onClick={() => setShowChapterSettings(true)} title={authorNote ? "Edit author's note" : "Add an author's note (shown at the end of the chapter)"} className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800", authorNote ? "text-primary" : "text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300")}><StickyNote className="h-3.5 w-3.5" /></button>;
      $[94] = authorNote;
      $[95] = isNotes;
      $[96] = t38;
    } else {
      t38 = $[96];
    }
    let t39;
    if ($[97] !== t37 || $[98] !== t38) {
      t39 = <div className="flex min-w-0 items-center gap-1">{t37}{t38}</div>;
      $[97] = t37;
      $[98] = t38;
      $[99] = t39;
    } else {
      t39 = $[99];
    }
    let t40;
    if ($[100] !== documentControls || $[101] !== t39) {
      t40 = <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">{documentControls}{t39}</div>;
      $[100] = documentControls;
      $[101] = t39;
      $[102] = t40;
    } else {
      t40 = $[102];
    }
    const t41 = hidePublication && "flex-wrap gap-2";
    let t42;
    if ($[103] !== t41) {
      t42 = cn("flex items-center gap-3", t41);
      $[103] = t41;
      $[104] = t42;
    } else {
      t42 = $[104];
    }
    let t43;
    if ($[105] !== backend.isDemo || $[106] !== chapterId || $[107] !== hidePublication || $[108] !== isDirty || $[109] !== isSaving || $[110] !== isTogglingStatus || $[111] !== onSave || $[112] !== scheduledReleaseAt || $[113] !== status || $[114] !== storyId) {
      t43 = status === "draft" && !backend.isDemo && !hidePublication && <ReleaseScheduleButton storyId={storyId} selection={{
        mode: "explicit",
        chapterIds: [chapterId]
      }} disabled={isSaving || isTogglingStatus} onBusy={setIsScheduling} beforeOpen={async () => !isDirty || (await onSave())}>{scheduledReleaseAt ? "Edit schedule" : "Schedule"}</ReleaseScheduleButton>;
      $[105] = backend.isDemo;
      $[106] = chapterId;
      $[107] = hidePublication;
      $[108] = isDirty;
      $[109] = isSaving;
      $[110] = isTogglingStatus;
      $[111] = onSave;
      $[112] = scheduledReleaseAt;
      $[113] = status;
      $[114] = storyId;
      $[115] = t43;
    } else {
      t43 = $[115];
    }
    let t44;
    if ($[116] !== handleToggleStatus || $[117] !== hidePublication || $[118] !== isScheduling || $[119] !== isTogglingStatus || $[120] !== status) {
      t44 = !hidePublication && (status === "published" ? <button type="button" onClick={handleToggleStatus} disabled={isTogglingStatus || isScheduling} title={"Published \u2014 click to unpublish (revert to draft)"} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50">{isTogglingStatus ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}Published</button> : <button type="button" onClick={handleToggleStatus} disabled={isTogglingStatus || isScheduling} title="Publish this chapter so readers can see it" className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-1.5 text-xs font-semibold text-white shadow-sm shadow-brand/30 transition-all hover:bg-brand/90 hover:shadow-md hover:shadow-brand/40 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed">{isTogglingStatus ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}{isTogglingStatus ? "Publishing\u2026" : "Publish"}</button>);
      $[116] = handleToggleStatus;
      $[117] = hidePublication;
      $[118] = isScheduling;
      $[119] = isTogglingStatus;
      $[120] = status;
      $[121] = t44;
    } else {
      t44 = $[121];
    }
    let t45;
    if ($[122] !== wordCount) {
      t45 = wordCount.toLocaleString();
      $[122] = wordCount;
      $[123] = t45;
    } else {
      t45 = $[123];
    }
    let t46;
    if ($[124] !== t45) {
      t46 = <span className="text-xs text-zinc-400 dark:text-zinc-500 tabular-nums">{t45} words</span>;
      $[124] = t45;
      $[125] = t46;
    } else {
      t46 = $[125];
    }
    let t47;
    if ($[126] !== isDirty || $[127] !== isSaving || $[128] !== saveError) {
      t47 = isDirty && !isSaving && !saveError && <span className="text-xs text-amber-500 font-medium">Unsaved</span>;
      $[126] = isDirty;
      $[127] = isSaving;
      $[128] = saveError;
      $[129] = t47;
    } else {
      t47 = $[129];
    }
    let t48;
    if ($[130] !== isSaving) {
      t48 = isSaving && <span className="text-xs text-blue-500 font-medium">Saving…</span>;
      $[130] = isSaving;
      $[131] = t48;
    } else {
      t48 = $[131];
    }
    const t49 = isDirty ? "default" : "ghost";
    const t50 = !isDirty || isSaving || isTogglingStatus;
    const t51 = isSaving ? "Saving\u2026" : saveError && isDirty ? "Retry save" : isDirty ? "Save" : "Saved";
    let t52;
    if ($[132] !== onSave || $[133] !== t49 || $[134] !== t50 || $[135] !== t51) {
      t52 = <Button size="sm" variant={t49} onClick={onSave} disabled={t50} className="h-7 px-3 text-xs">{t51}</Button>;
      $[132] = onSave;
      $[133] = t49;
      $[134] = t50;
      $[135] = t51;
      $[136] = t52;
    } else {
      t52 = $[136];
    }
    let t53;
    if ($[137] !== publicationControls || $[138] !== t42 || $[139] !== t43 || $[140] !== t44 || $[141] !== t46 || $[142] !== t47 || $[143] !== t48 || $[144] !== t52) {
      t53 = <div className={t42}>{t43}{publicationControls}{t44}{t46}{t47}{t48}{t52}</div>;
      $[137] = publicationControls;
      $[138] = t42;
      $[139] = t43;
      $[140] = t44;
      $[141] = t46;
      $[142] = t47;
      $[143] = t48;
      $[144] = t52;
      $[145] = t53;
    } else {
      t53 = $[145];
    }
    if ($[146] !== t34 || $[147] !== t40 || $[148] !== t53) {
      t30 = <div className={t34}>{t40}{t53}</div>;
      $[146] = t34;
      $[147] = t40;
      $[148] = t53;
      $[149] = t30;
    } else {
      t30 = $[149];
    }
    if ($[150] !== saveError) {
      t31 = saveError && <p role="alert" className="mb-2 text-sm text-destructive">{saveError}</p>;
      $[150] = saveError;
      $[151] = t31;
    } else {
      t31 = $[151];
    }
    let t54;
    if ($[152] !== textTools) {
      t54 = textTools.map(_temp2);
      $[152] = textTools;
      $[153] = t54;
    } else {
      t54 = $[153];
    }
    let t55;
    if ($[154] !== backend.isDemo) {
      t55 = !backend.isDemo && <div className="ml-auto"><EditorDesktopWritingTools /></div>;
      $[154] = backend.isDemo;
      $[155] = t55;
    } else {
      t55 = $[155];
    }
    t32 = <div className="hidden md:flex items-center gap-0.5 flex-wrap">{t54}{overflowGroups.map(_temp4)}{t55}</div>;
    t27 = "flex md:hidden items-center gap-0.5";
    if ($[156] !== textTools) {
      t28 = textTools.map(_temp5);
      $[156] = textTools;
      $[157] = t28;
    } else {
      t28 = $[157];
    }
    T1 = DropdownMenu;
    if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <DropdownMenuTrigger className="ml-auto inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-zinc-600 dark:text-zinc-400 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100 outline-none" aria-label="More formatting tools"><MoreHorizontal className="h-4 w-4" /><span>More</span></DropdownMenuTrigger>;
      $[158] = t26;
    } else {
      t26 = $[158];
    }
    T0 = DropdownMenuContent;
    t23 = "end";
    if ($[159] !== editor.commands) {
      t24 = e_0 => {
        e_0.preventDefault();
        editor.commands.focus();
      };
      $[159] = editor.commands;
      $[160] = t24;
    } else {
      t24 = $[160];
    }
    t25 = overflowGroups.map(_temp7);
    $[42] = authorNote;
    $[43] = backend.isDemo;
    $[44] = chapterId;
    $[45] = chapterTitle;
    $[46] = documentControls;
    $[47] = editor;
    $[48] = editorState.canRedo;
    $[49] = editorState.canUndo;
    $[50] = editorState.isAlignedCenter;
    $[51] = editorState.isAlignedLeft;
    $[52] = editorState.isAlignedRight;
    $[53] = editorState.isBlockquote;
    $[54] = editorState.isBulletList;
    $[55] = editorState.isH1;
    $[56] = editorState.isH2;
    $[57] = editorState.isH3;
    $[58] = editorState.isOrderedList;
    $[59] = handleToggleStatus;
    $[60] = hidePublication;
    $[61] = isDirty;
    $[62] = isNotes;
    $[63] = isSaving;
    $[64] = isScheduling;
    $[65] = isTogglingStatus;
    $[66] = isUploadingImage;
    $[67] = onSave;
    $[68] = openImagePicker;
    $[69] = publicationControls;
    $[70] = saveError;
    $[71] = scheduledReleaseAt;
    $[72] = status;
    $[73] = storyId;
    $[74] = t22;
    $[75] = textTools;
    $[76] = wordCount;
    $[77] = T0;
    $[78] = T1;
    $[79] = t23;
    $[80] = t24;
    $[81] = t25;
    $[82] = t26;
    $[83] = t27;
    $[84] = t28;
    $[85] = t29;
    $[86] = t30;
    $[87] = t31;
    $[88] = t32;
  } else {
    T0 = $[77];
    T1 = $[78];
    t23 = $[79];
    t24 = $[80];
    t25 = $[81];
    t26 = $[82];
    t27 = $[83];
    t28 = $[84];
    t29 = $[85];
    t30 = $[86];
    t31 = $[87];
    t32 = $[88];
  }
  let t33;
  if ($[161] !== T0 || $[162] !== t23 || $[163] !== t24 || $[164] !== t25) {
    t33 = <T0 align={t23} onCloseAutoFocus={t24}>{t25}</T0>;
    $[161] = T0;
    $[162] = t23;
    $[163] = t24;
    $[164] = t25;
    $[165] = t33;
  } else {
    t33 = $[165];
  }
  let t34;
  if ($[166] !== T1 || $[167] !== t26 || $[168] !== t33) {
    t34 = <T1>{t26}{t33}</T1>;
    $[166] = T1;
    $[167] = t26;
    $[168] = t33;
    $[169] = t34;
  } else {
    t34 = $[169];
  }
  let t35;
  if ($[170] !== t27 || $[171] !== t28 || $[172] !== t34) {
    t35 = <div className={t27}>{t28}{t34}</div>;
    $[170] = t27;
    $[171] = t28;
    $[172] = t34;
    $[173] = t35;
  } else {
    t35 = $[173];
  }
  let t36;
  if ($[174] !== handleImageInputChange || $[175] !== imageInputRef) {
    t36 = <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImageInputChange} className="hidden" />;
    $[174] = handleImageInputChange;
    $[175] = imageInputRef;
    $[176] = t36;
  } else {
    t36 = $[176];
  }
  let t37;
  if ($[177] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = () => setStoryIsPublic(true);
    $[177] = t37;
  } else {
    t37 = $[177];
  }
  let t38;
  if ($[178] !== showPublishStoryPrompt || $[179] !== storyId) {
    t38 = <PublishStoryPromptDialog storyId={storyId} open={showPublishStoryPrompt} onOpenChange={setShowPublishStoryPrompt} onStoryPublished={t37} />;
    $[178] = showPublishStoryPrompt;
    $[179] = storyId;
    $[180] = t38;
  } else {
    t38 = $[180];
  }
  let t39;
  if ($[181] !== authorNote || $[182] !== chapterId || $[183] !== chapterTitle || $[184] !== showChapterSettings) {
    t39 = showChapterSettings && <ChapterSettingsDialog chapterId={chapterId} chapterTitle={chapterTitle} authorNote={authorNote} open={showChapterSettings} onOpenChange={setShowChapterSettings} />;
    $[181] = authorNote;
    $[182] = chapterId;
    $[183] = chapterTitle;
    $[184] = showChapterSettings;
    $[185] = t39;
  } else {
    t39 = $[185];
  }
  let t40;
  if ($[186] !== t29 || $[187] !== t30 || $[188] !== t31 || $[189] !== t32 || $[190] !== t35 || $[191] !== t36 || $[192] !== t38 || $[193] !== t39) {
    t40 = <div className={t29}>{t30}{t31}{t32}{t35}{t36}{t38}{t39}</div>;
    $[186] = t29;
    $[187] = t30;
    $[188] = t31;
    $[189] = t32;
    $[190] = t35;
    $[191] = t36;
    $[192] = t38;
    $[193] = t39;
    $[194] = t40;
  } else {
    t40 = $[194];
  }
  return t40;
}
function _temp7(group_0, gi_0) {
  return <Fragment key={gi_0}>{gi_0 > 0 && <DropdownMenuSeparator />}{group_0.map(_temp6)}</Fragment>;
}
function _temp6(t_2) {
  return <DropdownMenuItem key={t_2.id} disabled={t_2.disabled} onSelect={t_2.run} className={cn("cursor-pointer gap-2", t_2.active && "bg-accent text-accent-foreground")}>{t_2.icon}<span className="flex-1">{t_2.label}</span>{t_2.shortcut && <DropdownMenuShortcut>{t_2.shortcut}</DropdownMenuShortcut>}</DropdownMenuItem>;
}
function _temp5(t_1) {
  return <ToolbarButton key={t_1.id} tool={t_1} />;
}
function _temp4(group, gi) {
  return <Fragment key={gi}><Divider />{group.map(_temp3)}</Fragment>;
}
function _temp3(t_0) {
  return <ToolbarButton key={t_0.id} tool={t_0} />;
}
function _temp2(t) {
  return <ToolbarButton key={t.id} tool={t} />;
}
function _temp(t0) {
  const {
    editor: e
  } = t0;
  return {
    isBold: e?.isActive("bold") ?? false,
    isItalic: e?.isActive("italic") ?? false,
    isUnderline: e?.isActive("underline") ?? false,
    isStrike: e?.isActive("strike") ?? false,
    isH1: e?.isActive("heading", {
      level: 1
    }) ?? false,
    isH2: e?.isActive("heading", {
      level: 2
    }) ?? false,
    isH3: e?.isActive("heading", {
      level: 3
    }) ?? false,
    isBulletList: e?.isActive("bulletList") ?? false,
    isOrderedList: e?.isActive("orderedList") ?? false,
    isBlockquote: e?.isActive("blockquote") ?? false,
    isAlignedLeft: (e?.isActive({
      textAlign: "left"
    }) || e?.isActive({
      textAlign: null
    })) ?? false,
    isAlignedCenter: e?.isActive({
      textAlign: "center"
    }) ?? false,
    isAlignedRight: e?.isActive({
      textAlign: "right"
    }) ?? false,
    canUndo: e?.can().undo() ?? false,
    canRedo: e?.can().redo() ?? false
  };
}
function Divider() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-5 w-px bg-zinc-200 dark:bg-zinc-700 mx-1" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ToolbarButton(t0) {
  const $ = _c(10);
  const {
    tool
  } = t0;
  const t1 = tool.run;
  const t2 = tool.disabled;
  const t3 = tool.active;
  const t4 = tool.shortcut ? `${tool.label} (${tool.shortcut})` : tool.label;
  const t5 = tool.active ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400" : "text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-zinc-100";
  const t6 = tool.disabled ? "opacity-30 cursor-not-allowed" : "cursor-pointer";
  let t7;
  if ($[0] !== t5 || $[1] !== t6) {
    t7 = cn("flex h-7 w-7 items-center justify-center rounded-sm transition-colors", t5, t6);
    $[0] = t5;
    $[1] = t6;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  let t8;
  if ($[3] !== t4 || $[4] !== t7 || $[5] !== tool.active || $[6] !== tool.disabled || $[7] !== tool.icon || $[8] !== tool.run) {
    t8 = <button type="button" onClick={t1} disabled={t2} aria-pressed={t3} title={t4} className={t7}>{tool.icon}</button>;
    $[3] = t4;
    $[4] = t7;
    $[5] = tool.active;
    $[6] = tool.disabled;
    $[7] = tool.icon;
    $[8] = tool.run;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
}